import { groupBy, map } from 'lodash/fp';
import moment from 'moment';
import React from 'react';
import { Card } from '../../../../components/Cards';
import TimePicker from '../../../../components/DateTime/TimePicker';
import { FormLabel } from '../../../../components/Form/FormLabel';
import { Option, OptionHeader, Select } from '../../../../components/Select';
import * as labels from '../constants';
import {
    getAllTimezones,
    getDateFormats,
    getDateTimeFormat,
    getEndOfBusinessHour,
    getMinuteStepSize,
    getPreferredTimezones,
    getSelectedDateFormat,
    getSelectedTimeFormat,
    getSelectedTimezone,
    getStartOfBusinessHour,
    getTimeFormats,
    is12hrFormat,
    setEndOfBusinessHour,
    setMinuteStepSize,
    setSelectedDateFormat,
    setSelectedTimeFormat,
    setSelectedTimezone,
    setStartOfBusinessHour,
} from '../model';
import { ModelProps } from '../presenter';
import styles from '../style.m.less';
import { DateFormatDto, TimeFormatDto } from '../types';
import SettingsRow from './SettingsRow';

// @ts-ignore
const mapWithKey = map.convert({ cap: false });

const AdminDateTime = ({ model, setModel }: ModelProps) => (
    <div id="dateTimeSettings">
        <h2>Date and Time</h2>
        <Card>
            <SettingsRow label="Timezone">
                <Timezone model={model} setModel={setModel} />
            </SettingsRow>
            <SettingsRow label="Date and Time Format">
                <TimeFormat model={model} setModel={setModel} />
            </SettingsRow>
            <SettingsRow label="Default Business Day">
                <EndOfBusinessHour model={model} setModel={setModel} />
            </SettingsRow>
        </Card>
    </div>
);

const Timezone = ({ model, setModel }: ModelProps) => {
    const selectTimeZoneProps = {
        selected: getSelectedTimezone(model),
        optionToText: (timezone: string) => timezone,
        onSelect: (timezone: string) => setModel(setSelectedTimezone(timezone, model)),
    };

    return (
        <>
            <Select {...selectTimeZoneProps}>
                <OptionHeader name="Preferred timezones" />
                {map(
                    timeZone => (
                        <Option key={timeZone} value={timeZone}>
                            {timeZone}
                        </Option>
                    ),
                    getPreferredTimezones(model)
                )}
                <OptionHeader name="All timezones" />
                {map(
                    timeZone => (
                        <Option key={timeZone} value={timeZone}>
                            {timeZone}
                        </Option>
                    ),
                    getAllTimezones(model)
                )}
            </Select>
            <p className="caption">{labels.timezone}</p>
        </>
    );
};

const OptionsFromGroup = ({ formatGroup }: { formatGroup: DateFormatDto[] }) => (
    <>
        {map(
            dateFormat => (
                <Option key={dateFormat.format} value={dateFormat}>
                    {moment().format(dateFormat.format)}
                </Option>
            ),
            formatGroup
        )}
    </>
);

const TimeFormat = ({ model, setModel }: ModelProps) => {
    const selectDateFormatProps = {
        selected: getSelectedDateFormat(model),
        optionToText: (df: DateFormatDto) => moment().format(df.format),
        onSelect: (df: DateFormatDto) => setModel(setSelectedDateFormat(df, model)),
    };

    const selectTimeFormatProps = {
        selected: getSelectedTimeFormat(model),
        optionToText: (tf: TimeFormatDto) => tf.name,
        onSelect: (tf: TimeFormatDto) => setModel(setSelectedTimeFormat(tf, model)),
    };

    const selectMinuteStepSizeProps = {
        selected: getMinuteStepSize(model),
        optionToText: (ti: number) => `${ti} min`,
        onSelect: (ti: number) => setModel(setMinuteStepSize(ti, model)),
    };

    return (
        <>
            <FormLabel>Date Format</FormLabel>
            <Select {...selectDateFormatProps}>
                {mapWithKey(
                    (formatGroup: DateFormatDto[], key: string) => (
                        <React.Fragment key={key}>
                            <OptionHeader name={key} />
                            <OptionsFromGroup formatGroup={formatGroup} />
                        </React.Fragment>
                    ),
                    groupBy('name', getDateFormats(model))
                )}
            </Select>
            <FormLabel>Time Format</FormLabel>
            <Select {...selectTimeFormatProps}>
                {map(
                    timeFormat => (
                        <Option key={timeFormat.format} value={timeFormat}>
                            {timeFormat.name}
                        </Option>
                    ),
                    getTimeFormats(model)
                )}
            </Select>
            <FormLabel>Minute Increments</FormLabel>
            <Select {...selectMinuteStepSizeProps}>
                {map(
                    interval => (
                        <Option key={interval} value={interval}>
                            {`${interval} min`}
                        </Option>
                    ),
                    [1, 5, 10, 15]
                )}
            </Select>
            <p className="caption">{labels.timeFormat(getDateTimeFormat(model))}</p>
        </>
    );
};

const EndOfBusinessHour = ({ model, setModel }: ModelProps) => (
    <>
        <FormLabel>Start of Business Day</FormLabel>
        <span className={styles.pickerMiddle}>
            <TimePicker
                kind="DEFAULT"
                timeZone={getSelectedTimezone(model)}
                value={getStartOfBusinessHour(model)}
                onChange={time => setModel(setStartOfBusinessHour(time, model))}
                stepSizeMins={0}
                format={is12hrFormat(model) ? '12hr' : '24hr'}
            />
        </span>
        <FormLabel>End of Business Day</FormLabel>
        <span className={styles.pickerMiddle}>
            <TimePicker
                kind="DEFAULT"
                timeZone={getSelectedTimezone(model)}
                value={getEndOfBusinessHour(model)}
                onChange={time => setModel(setEndOfBusinessHour(time, model))}
                stepSizeMins={0}
                format={is12hrFormat(model) ? '12hr' : '24hr'}
            />
        </span>
        <p className="caption">{labels.endofBusinessHour}</p>
    </>
);

export default AdminDateTime;
